// Catalog.ID Client — Operator Review Pane
// ============================================================================
// For operator sessions only. Shows pending publication requests.
// Operators can approve or reject publications.

import {
    createListPaneType,
    createElement,
    createFieldGroup,
    createFormRow,
    renderFormField,
    createEmptyState,
    renderPaneActions,
} from '@catalog-shell';
import type {
    ListPaneConfig, PaneActionDescriptor, PaneInstance, PaneType, PaneTypeRenderContext,
    FormRendererResult,
} from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import { claimTypeLabel } from './claims-list-pane';
import * as publicClaimsApi from '../api/public-claims';
import * as claimsApi from '../api/claims';
import type { ClaimDetail } from '../api/types';
import { decryptClaim, unwrapSymmetricKeyChain } from '../crypto/claims';
import * as aes from '../crypto/aes';
import { keys } from '../state/keys';
import { identity } from '../state/identity';
import { session } from '../state/session';
import { getOrgAttrKeyChain } from '../state/org-keys';
import { renderClaimStatusSection } from '../components/claim-status-section';
import type { ClaimKeyEntry, PendingPublicClaim } from '../api/types';
import { PANE_IDS } from '../shell-config';

// operatorSignature is computed server-side with the node's signing key and is NOT a
// client input. The reviewing session's role here is authorization (derived from an
// active operator↔org binding plus the delegate's org role — operator-org-binding
// plan §2.7); the artifact is signed by the node, not by the reviewer's account.

/**
 * The claim-key rows a reviewer should try, in order. A publication shares its
 * key with EVERY operator organization, so the claim carries one `share` row
 * per operator and the server lists them in no particular order; the row this
 * session can open is the one addressed to ITS organization — a delegate's
 * organization cid, or the session's own cid when it is the org account.
 * Share rows whose recipient could not be resolved (or belong to another
 * operator) follow as a fallback, and the `self` row comes last for
 * self-review of the reviewer's own claim. Anything else is not a review key.
 */
export function orderReviewKeys(keys: ClaimKeyEntry[], reviewerCids: (string | null)[]): ClaimKeyEntry[] {
    const mine = new Set(reviewerCids.filter((c): c is string => !!c));
    const share = keys.filter(k => k.recipientType === 'share');
    const addressed = (k: ClaimKeyEntry) => k.recipientCid !== null && mine.has(k.recipientCid);
    return [
        ...share.filter(addressed),
        ...share.filter(k => !addressed(k)),
        ...keys.filter(k => k.recipientType === 'self'),
    ];
}

/**
 * Decrypt a claim with one candidate attr_encrypt key chain. Returns null when
 * this chain is not the right one, so the caller can try the next.
 */
export async function tryDecryptWithChain(
    detail: ClaimDetail,
    key: ClaimKeyEntry,
    chain: { keyIndex: number; secretKey: Uint8Array }[],
): Promise<string | null> {
    if (chain.length === 0) return null;
    try {
        if (key.keyIndex > 0) {
            // Post-rotation: peel the envelopes from keyIndex down to 0.
            const symKey = await unwrapSymmetricKeyChain(key.encryptedKey, chain, key.keyIndex);
            const ct = aes.fromBase64(detail.encryptedValue);
            const n = aes.fromBase64(detail.nonce);
            return aes.decodeUtf8(await aes.decrypt(symKey, n, ct));
        }
        // Single layer: the sharer wrapped the key to whichever org public key
        // the server advertised as active at the time — key 0 for an org that
        // never rotated, the newest key after a rotation (the old public key is
        // revoked, yet the share row's keyIndex stays 0 because the server never
        // learns which key was used). Try each, original first.
        for (const c of [...chain].sort((a, b) => a.keyIndex - b.keyIndex)) {
            try {
                return await decryptClaim(
                    detail.encryptedValue, detail.nonce, key.encryptedKey, c.secretKey,
                );
            } catch { /* not this key — try the next */ }
        }
        return null;
    } catch {
        return null;
    }
}

// ============================================================================
// Operator Review List
// ============================================================================

const columns: ColumnDef<PendingPublicClaim>[] = [
    {
        key: 'claimType',
        header: 'Type',
        width: 120,
        getValue: (item) => item.claimType,
        type: {
            kind: 'text',
            converter: (v) => claimTypeLabel(v as string),
        },
    },
    {
        key: 'ownerCid',
        header: 'Owner',
        width: 140,
        getValue: (item) => item.ownerCid,
        type: { kind: 'text' },
    },
    {
        key: 'ownerAccountType',
        header: 'Identity',
        width: 100,
        getValue: (item) => item.ownerAccountType,
        type: {
            kind: 'text',
            converter: (v) => {
                const labels: Record<string, string> = {
                    natural_person: 'Personal', organization: 'Organization',
                    delegate: 'Delegate', agent: 'Agent',
                };
                return labels[v as string] ?? String(v);
            },
        },
    },
    {
        key: 'creationTime',
        header: 'Requested',
        width: 100,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
    },
];

function createPendingDataSource(): DataSource<PendingPublicClaim> {
    return new DataSource<PendingPublicClaim>({
        fetch: async (params): Promise<FetchResult<PendingPublicClaim>> => {
            let claims: PendingPublicClaim[];
            try {
                claims = await publicClaimsApi.listPendingPublicClaims();
            } catch (err) {
                // Powers can be revoked mid-session now (binding release or role
                // change), and the workspace stays visible until the next login —
                // so say what happened rather than surfacing a bare 403.
                const revoked = publicClaimsApi.operatorAccessRevokedMessage(err);
                throw revoked ? new Error(revoked) : err;
            }
            const sliced = claims.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: claims.length };
        },
        keyFn: (item) => String(item.publicClaimId),
        pageSize: 100,
    });
}

const listConfig: ListPaneConfig<PendingPublicClaim> = {
    typeId: PANE_IDS.operatorReview,
    defaultTitle: 'Publication Review',
    createDataSource: () => createPendingDataSource(),
    columns,
    keyFn: (item) => String(item.publicClaimId),
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'publicclaims') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.operatorReviewDetail,
            title: `Review: ${claimTypeLabel(item.claimType)}`,
            context: { pendingClaim: item },
        }, { originPaneId: ctx.instance.instanceId });
    },
    ariaLabel: 'Pending publication requests',
};

export function createOperatorReviewPane() {
    return createListPaneType(listConfig);
}

// ============================================================================
// Operator Review Detail
// ============================================================================

// ----------------------------------------------------------------------------
// Title-row actions
//
// Approve and Reject are the reviewer's verbs on the claim this pane is showing,
// so they ride in its title row (shell USAGE, "Where a button belongs"); the
// rejection reason stays in the body, next to the evidence it is written about.
// Both refusals are stated on the button: Approve publishes the DECRYPTED value,
// so it stays out of reach while decryption failed, and a rejection the owner
// cannot act on is no rejection, so Reject waits for a reason.
// ----------------------------------------------------------------------------

/** What the title row needs to know about the review in progress. */
export interface OperatorReviewActionState {
    /** The claim has been fetched and the decrypt attempt has settled. */
    loaded: boolean;
    decryptionFailed: boolean;
    reasonEmpty: boolean;
    /** A decision is in flight — neither verb may be pressed again. */
    busy: boolean;
}

interface OperatorReviewState extends OperatorReviewActionState {
    ctx: PaneTypeRenderContext;
    pending: PendingPublicClaim;
    decryptedValue: string;
    getReason: () => string;
}

const states = new WeakMap<PaneInstance, OperatorReviewState>();

const CANNOT_APPROVE = 'The claim value could not be decrypted, so there is nothing to publish';
const DECISION_IN_FLIGHT = 'Recording your decision…';

/** Pure: the two verbs, and what each one is waiting for. */
export function planOperatorReviewActions(
    st: OperatorReviewActionState | undefined,
    exec: (decision: 'approve' | 'reject') => void,
): PaneActionDescriptor[] {
    if (!st?.loaded) return [];
    const approveBlocked = st.decryptionFailed ? CANNOT_APPROVE : st.busy ? DECISION_IN_FLIGHT : undefined;
    const rejectBlocked = st.reasonEmpty
        ? 'Enter a rejection reason'
        : st.busy ? DECISION_IN_FLIGHT : undefined;
    return [
        {
            key: 'approve',
            label: 'Approve',
            variant: 'primary',
            disabled: Boolean(approveBlocked),
            title: approveBlocked,
            execute: () => exec('approve'),
        },
        {
            key: 'reject',
            label: 'Reject',
            variant: 'danger',
            disabled: Boolean(rejectBlocked),
            title: rejectBlocked,
            execute: () => exec('reject'),
        },
    ];
}

function operatorReviewActions(instance: PaneInstance): HTMLElement[] {
    const st = states.get(instance);
    return renderPaneActions(planOperatorReviewActions(st, (decision) => {
        void submitDecision(st!, decision);
    }));
}

async function submitDecision(st: OperatorReviewState, decision: 'approve' | 'reject'): Promise<void> {
    if (st.busy) return;
    const { ctx, pending } = st;
    const reason = st.getReason().trim();
    if (decision === 'reject' && !reason) return;   // the button says so; belt and braces

    st.busy = true;
    ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    try {
        await publicClaimsApi.reviewPublicClaim(pending.publicClaimId, decision === 'approve'
            ? { approved: true, publicValue: st.decryptedValue }
            : { approved: false, rejectionReason: reason });
        ctx.shell.emit('data-changed', { type: 'publicclaims' });
        ctx.shell.toast(decision === 'approve' ? 'Claim approved and published' : 'Claim rejected', 'success');
        const surface = ctx.shell.getSurfaceForPane(ctx.instance.instanceId);
        surface?.closePane(ctx.instance.instanceId);
    } catch (err) {
        const fallback = decision === 'approve' ? 'Approval failed' : 'Rejection failed';
        ctx.shell.toast(err instanceof Error ? err.message : fallback, 'error');
        st.busy = false;
        ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    }
}

export function createOperatorReviewDetailPane(): PaneType {
    return {
        typeId: PANE_IDS.operatorReviewDetail,
        kind: 'leaf',
        defaultTitle: 'Review Claim',
        capabilities: [],
        tabBarActions: operatorReviewActions,
        onDestroy: (instance) => { states.delete(instance); },
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const pending = ctx.instance.context['pendingClaim'] as PendingPublicClaim | undefined;

            if (!pending) {
                container.appendChild(createEmptyState({ message: 'No claim selected' }));
                return container;
            }

            const st: OperatorReviewState = {
                ctx,
                pending,
                loaded: false,
                decryptionFailed: false,
                decryptedValue: '',
                reasonEmpty: true,
                busy: false,
                getReason: () => '',
            };
            states.set(ctx.instance, st);
            void renderReviewDetail(container, st);
            return container;
        },
    };
}

async function renderReviewDetail(container: HTMLElement, st: OperatorReviewState): Promise<void> {
    const { ctx, pending } = st;
    // Info section
    const infoGroup = createFieldGroup({ title: 'Claim Information' });

    const typeRow = createFormRow({ label: 'Type' });
    typeRow.content.appendChild(createElement('span', { textContent: claimTypeLabel(pending.claimType) }));
    infoGroup.body.appendChild(typeRow.element);

    const ownerRow = createFormRow({ label: 'Owner' });
    ownerRow.content.appendChild(createElement('span', {
        className: 'cid-profile-cid',
        textContent: pending.ownerCid,
    }));
    infoGroup.body.appendChild(ownerRow.element);

    const dateRow = createFormRow({ label: 'Requested' });
    dateRow.content.appendChild(createElement('span', {
        textContent: new Date(pending.creationTime).toLocaleString(),
    }));
    infoGroup.body.appendChild(dateRow.element);

    container.appendChild(infoGroup.element);

    // Decrypt the claim value (operator must have keys loaded and claim shared with them)
    const valueGroup = createFieldGroup({ title: 'Claim Value' });
    let decryptedValue = '';
    let loadedDetail: ClaimDetail | null = null;

    let decryptionFailed = false;
    try {
        loadedDetail = await claimsApi.getClaim(pending.claimUuid);
        if (!keys.getPrivateKey('attr_encrypt')) {
            decryptedValue = '[operator keys not loaded]';
            decryptionFailed = true;
        } else {
            // One `share` row per operator organization; the one this session
            // can open is addressed to ITS organization (operator-org-binding
            // plan §2.7) — ordered by recipient, see orderReviewKeys.
            const rows = orderReviewKeys(loadedDetail.keys, [identity.getOrganizationCid(), session.getCid()]);
            if (rows.length === 0) {
                decryptedValue = '[claim not shared with operator]';
                decryptionFailed = true;
            } else {
                // Publishers wrap the review key to the org's attr_encrypt key,
                // which a delegate holds only in wrapped form — so the org chain
                // is tried first. The session's own chain still covers an
                // org-account session and self-review of the reviewer's own claim.
                const chains = [await getOrgAttrKeyChain(), keys.getKeyChain('attr_encrypt')];
                let value: string | null = null;
                for (const row of rows) {
                    for (const chain of chains) {
                        value = await tryDecryptWithChain(loadedDetail, row, chain);
                        if (value !== null) break;
                    }
                    if (value !== null) break;
                }
                if (value === null) {
                    decryptedValue = '[cannot decrypt — claim key is not addressed to this operator]';
                    decryptionFailed = true;
                } else {
                    decryptedValue = value;
                }
            }
        }
    } catch {
        decryptedValue = '[failed to load claim]';
        decryptionFailed = true;
    }

    const valueRow = createFormRow({ label: 'Value' });
    valueRow.content.appendChild(createElement('span', { textContent: decryptedValue }));
    valueGroup.body.appendChild(valueRow.element);
    container.appendChild(valueGroup.element);

    // Validation status and certificates
    if (loadedDetail) {
        renderClaimStatusSection(container, loadedDetail);
    }

    // Review section — the reason the reviewer may type; the verbs themselves
    // are in the title row.
    const actionGroup = createFieldGroup({ title: 'Review Action' });

    const reasonRow = createFormRow({ label: 'Rejection Reason' });
    const reasonField: FormRendererResult = renderFormField(
        { type: 'text', key: 'reason', label: 'Rejection Reason', placeholder: 'Required if rejecting' },
        '',
        (value) => {
            // Reject is gated on this field, so the row is re-planned the moment
            // it crosses between empty and non-empty — and only then.
            const empty = !String(value ?? '').trim();
            if (empty === st.reasonEmpty) return;
            st.reasonEmpty = empty;
            ctx.shell.refreshPaneActions(ctx.instance.instanceId);
        },
    );
    reasonRow.content.appendChild(reasonField.element);
    actionGroup.body.appendChild(reasonRow.element);
    container.appendChild(actionGroup.element);

    st.getReason = () => String(reasonField.getValue() ?? '');
    st.decryptedValue = decryptedValue;
    st.decryptionFailed = decryptionFailed;
    st.loaded = true;
    // The title row was built before the decrypt above; plan it from what the
    // reviewer can actually see now.
    ctx.shell.refreshPaneActions(ctx.instance.instanceId);
}
